/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { DataOptions } from "vuetify/types";
import { DocumentsListItem } from "@/models/api/documents/DocumentsList";
import { DocumentServiceItem } from "@/models/api/documents/DocumentServicesList";
import { DocumentPaymentListItem } from "@/models/api/documents/DocumentPaymentsList";
import {
  getDetailsDocumentToCancel,
  getDocumentPairFiles,
  getDocumentPaymentsTable,
  getDocumentServicesTable,
  getDocumentsTable,
  getDocumentTypes,
  isAvailableKsefOfflineVisualization,
  isAvailableKsefVisualization,
  postCancelDocument,
  postDuplicateDocument,
  sendToKsef,
  showDocument,
  showKsefVisualization,
  toggleKsefStopExport,
  trySetOffline,
  tryShowKsefOfflineVisualization,
} from "@/requests/document";
import { FILTER_NAMES } from "@/static/filterNames";
import { PROMPT_TYPE, PromptModel } from "@/models/components/popup/Prompt";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import requiredValidator from "@/rules/requiredValidator";
import dayjs from "dayjs";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { DEFAULT_DATE_FORMAT } from "@/static/dateFormats";
import SelectItem from "@/models/components/inputs/selectItem";
import DocumentsCorrectionNoteModule from "@/store/modules/documents/correctionNote";
import DocumentsVatTableModule from "@/store/modules/documents/vatTable";
import DocumentsPrintModule from "@/store/modules/documents/print";
import { FileGuid } from "@/models/api/Files";
import _ from "lodash";
import VATMarginSettlementsModule from "@/store/modules/documents/vatMarginSettlements";
import { DetailsDocumentToCancel } from "@/models/api/documents/DetailsDocumentToCancel";
import CustomDataTableHeader from "@/models/components/dataTables/CustomDataTableHeader";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";

export interface DocumentsStateInterface {
  isDocumentsLoading: boolean;
  isDocumentServicesLoading: boolean;
  isDocumentPaymentsLoading: boolean;
  documents: Array<DocumentsListItem>;
  documentsCount: number;
  documentsFilters: null | Array<CriterionItemRequest>;
  documentsDataTableOptions: DataOptions;
  selectedDocumentId: null | string;

  documentServices: Array<DocumentServiceItem>;
  documentPayments: Array<DocumentPaymentListItem>;

  documentTypes: null | Array<SelectItem>;

  pairedFilesDocumentId: null | string;
  pairedFiles: Array<FileGuid>;
  selectedPairedFile: null | FileGuid;

  cancelledDocument: {
    isLoading: boolean;
    documentId: null | string;
    documentNumber: string;
    isCorrection: boolean;
    hasBooking: boolean;
  };
  tableHeaders: Array<CustomDataTableHeader>;
}

export const DocumentsState: DocumentsStateInterface = {
  isDocumentsLoading: false,
  isDocumentServicesLoading: false,
  isDocumentPaymentsLoading: false,
  documents: [],
  documentsCount: -1,
  documentsFilters: null,
  documentsDataTableOptions: new DefaultDataOptions(),
  selectedDocumentId: null,

  documentServices: [],
  documentPayments: [],

  documentTypes: null,

  pairedFilesDocumentId: null,
  pairedFiles: [],
  selectedPairedFile: null,

  cancelledDocument: {
    isLoading: false,
    documentId: null,
    documentNumber: "",
    isCorrection: false,
    hasBooking: false,
  },
  tableHeaders: [],
};

const DocumentsModule = defineModule({
  modules: {
    correctionNotes: DocumentsCorrectionNoteModule,
    vatTable: DocumentsVatTableModule,
    print: DocumentsPrintModule,
    vatMarginSettlements: VATMarginSettlementsModule,
  },
  state: DocumentsState as DocumentsStateInterface,
  namespaced: true as const,
  getters: {
    isDocumentPaymentsLoading(...args): boolean {
      const { state } = _moduleGetterContext(args);
      return state.isDocumentsLoading || state.isDocumentPaymentsLoading;
    },
    isDocumentServicesLoading(...args): boolean {
      const { state } = _moduleGetterContext(args);
      return state.isDocumentsLoading || state.isDocumentServicesLoading;
    },
  },
  mutations: {
    setDataTableOptions(state, param: DataOptions) {
      state.documentsDataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setDocuments(state, param: Array<DocumentsListItem>) {
      state.documents = param;
      state.isDocumentsLoading = false;
    },
    setDocumentServices(state, param: Array<DocumentServiceItem>) {
      state.documentServices = param;
      state.isDocumentServicesLoading = false;
    },
    setDocumentPayments(state, param: Array<DocumentPaymentListItem>) {
      state.documentPayments = param;
      state.isDocumentPaymentsLoading = false;
    },
    setSelectedDocumentId(state, param: string | null) {
      state.selectedDocumentId = param;
    },
    startDocumentsLoading(state) {
      state.isDocumentsLoading = true;
    },
    startDocumentServicesLoading(state) {
      state.isDocumentServicesLoading = true;
    },
    startDocumentPaymentsLoading(state) {
      state.isDocumentPaymentsLoading = true;
    },
    setDocumentsCount(state, param: number) {
      state.documentsCount = param;
    },
    setDocumentFilters(state, param: Array<CriterionItemRequest>) {
      state.documentsFilters = param;
    },
    setDocumentTypes(state, param: null | Array<SelectItem>) {
      state.documentTypes = param;
    },
    setPairedFilesDocumentId(state, param: null | string) {
      state.pairedFilesDocumentId = param;
    },
    setPairedFiles(state, param: Array<FileGuid>) {
      state.pairedFiles = param;
    },
    setSelectedPairedFile(state, param: FileGuid) {
      state.selectedPairedFile = param;
    },
    setCancelledDocumentId(state, param: string | null) {
      state.cancelledDocument.documentId = param;
    },
    setIsCancelDocumentLoading(state, param: boolean) {
      state.cancelledDocument.isLoading = param;
    },
    setDetailsDocumentToCancel(state, param: DetailsDocumentToCancel) {
      state.cancelledDocument.isCorrection = param.isCorrection;
      state.cancelledDocument.documentNumber = param.documentNumber;
      state.cancelledDocument.hasBooking = param.hasBooking;
    },
    setTableHeaders(state, param: Array<CustomDataTableHeader>) {
      state.tableHeaders = param;
    },
  },
  actions: {
    async getDocumentsTable(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.startDocumentsLoading();
      const response = await getDocumentsTable({
        count: state.documentsDataTableOptions.itemsPerPage,
        page: state.documentsDataTableOptions.page - 1,
        criterions: state.documentsFilters === null ? null : _.cloneDeep(state.documentsFilters),
        filterName: FILTER_NAMES.DocumentsFilter,
      });
      commit.setDocuments(response.items);
      commit.setDocumentsCount(response.totalCount);
      if (response.items.length > 0) {
        commit.setSelectedDocumentId(response.items[0].id);
      }
    },

    async getDocumentServicesTable(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedDocumentId !== null) {
        commit.startDocumentServicesLoading();
        const response = await getDocumentServicesTable(state.selectedDocumentId);
        commit.setDocumentServices(response);
      }
    },
    async getDocumentPaymentsTable(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedDocumentId !== null) {
        commit.startDocumentPaymentsLoading();
        const response = await getDocumentPaymentsTable(state.selectedDocumentId);
        commit.setDocumentPayments(response);
      }
    },
    async duplicateDocument(context, param: DocumentsListItem) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      const title = filterCapitalize(i18n.t("documentDuplicateNumber")) + " <b>" + param.documentNumber + "</b>";
      const prompt = new PromptModel(title);
      prompt.type = PROMPT_TYPE.DATE;
      prompt.label = filterCapitalize(i18n.t("duplicateFromDate"));
      prompt.rules = [requiredValidator];
      prompt.min = param.issueDate;
      prompt.max = dayjs().format(DEFAULT_DATE_FORMAT);
      prompt.value = dayjs().format(DEFAULT_DATE_FORMAT);
      const date = await rootDispatch.popup.addPrompt(prompt);
      await postDuplicateDocument(param.id, date as string);
      await dispatch.getDocumentsTable();
    },
    async cancelDocument(context, param: { documentId: string; returnBookingToList: boolean }) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);

      const result = await postCancelDocument(param.documentId, param.returnBookingToList, false);
      if (result?.show) {
        const buttons = result.hasPermission
          ? [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ]
          : [
              {
                text: filterCapitalize(i18n.t("close")),
                color: "warning",
                value: false,
              },
            ];

        const secondConfirm = new ConfirmModel(
          result.message ?? "",
          filterCapitalize(filterCapitalize(i18n.t("warning"))),
          buttons
        );

        const isConfirmed = await rootDispatch.popup.addConfirm(secondConfirm);

        if (isConfirmed) {
          await postCancelDocument(param.documentId, param.returnBookingToList, true);
        }
      }

      await dispatch.getDocumentsTable();
      //TODO otworzyc anulowanego PDFA, showFile aktualnie nie działa
    },
    async openCancelDocumentDialog(context, item: DocumentsListItem) {
      const { commit } = _moduleActionContext(context);
      commit.setCancelledDocumentId(item.id);
      commit.setIsCancelDocumentLoading(true);
      const response = await getDetailsDocumentToCancel(item.id);
      commit.setDetailsDocumentToCancel(response);
      commit.setIsCancelDocumentLoading(false);
    },
    async showDocument(
      context,
      params: {
        documentId: string;
        originalCount: undefined | number;
        copyCount: undefined | number;
      }
    ) {
      await showDocument(params.documentId, undefined, params.originalCount, params.copyCount);
    },
    async getDocumentTypes(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.documentTypes === null) {
        commit.setDocumentTypes(await getDocumentTypes());
      }
    },
    async openPairedFilesDialog(context, documentId: string) {
      const { commit } = _moduleActionContext(context);
      commit.setPairedFiles([]);
      commit.setPairedFilesDocumentId(documentId);
      const documentFiles = await getDocumentPairFiles(documentId);
      commit.setPairedFiles(documentFiles);
      if (documentFiles.length > 0) {
        commit.setSelectedPairedFile(documentFiles[0]);
      }
    },
    async sendDocumentToKsef(context, documentId: string) {
      await sendToKsef(documentId);
    },
    async checkIsAvailableVisualization(context, documentId: string) {
      if (await isAvailableKsefVisualization(documentId)) {
        await showKsefVisualization(documentId);
      }
    },
    async toggleKsefStopExport(context, documentId: string) {
      await toggleKsefStopExport(documentId);
    },
    async canGenerateKsefOfflinePdf(context, documentId: string) {
      const { rootDispatch } = _moduleActionContext(context);
      const response = await isAvailableKsefOfflineVisualization(documentId);

      await DoubleConfirm.Process(
        response,
        rootDispatch.popup.addConfirm,
        async () => {
          await trySetOffline(documentId);
        },
        async () => {
          await tryShowKsefOfflineVisualization(documentId);
        }
      );
    },
    async getPairedFile(context, file: FileGuid) {
      const { commit } = _moduleActionContext(context);
      commit.setSelectedPairedFile(file);
    },
  },
});

export default DocumentsModule;

/* eslint-disable  @typescript-eslint/no-explicit-any */
//eslint-disable-next-line
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, DocumentsModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, DocumentsModule);
